/**
 * QuickEmailModal — Editorial-Atelier-styled modal that lets a planner
 * draft a contextual email and hand it off to their own email client
 * via mailto: (or copy it to paste wherever they like). Mirrors Mock 20.
 *
 * The modal is self-contained (no Redux / store coupling); the provider
 * opens/closes it and supplies the context. The only outward effects
 * are (a) firing mailto/clipboard and (b) writing an activity_log row.
 *
 * Props are intentionally shallow so callers can't drift from the
 * mock: `site` picks the template list, `recipients` pre-fills To,
 * `tokens` is the merge context, and `onHandoff` reports back what
 * happened (for toast + caller cleanup).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { buildTokenValues, resolveMergeTokens } from '../../lib/mergeTokens'
import { templatesForSite, QE_TEMPLATES } from './quickEmailTemplates'
import { openInEmailClient, copyToClipboard, logHandoff } from './quickEmailHandoff'

// ── visual tokens (keep inline to avoid stylesheet drift) ─────────────
const PAPER   = 'var(--paper, #F4EFE6)'
const CARD    = '#FFFFFF'
const INK     = 'var(--ink, #1A1D23)'
const INK_SOFT= 'var(--ink-soft, #3A3F48)'
const MUTE    = 'var(--mute, #8B8578)'
const BORDER  = 'var(--border, #E3DCC9)'
const CLAY    = 'var(--clay, #C65D45)'
const CLAY_DK = 'var(--clay-dark, #A84A36)'
const SAGE    = 'var(--sage, #7A8B6F)'
const GOLD    = 'var(--gold, #A8864F)'
const SERIF   = "'Fraunces', Georgia, serif"

// ── Venue-aware fragment helper ───────────────────────────────────────
// Many templates want "…on June 14{venue_suffix}" which becomes either
// " at Stags' Leap" or "" depending on whether the context knows the
// venue. We resolve the synthetic {venue_suffix} here rather than in
// mergeTokens.js because it's a composition of two tokens and clutter-
// ing the catalog with presentation-only synthetics isn't worth it.
function venueSuffix(values) {
  const v = values?.['{venue_name}']
  return v ? ` at ${v}` : ''
}

function applyTokens(str, values) {
  // Compose synthetics first, then resolve.
  const composed = String(str || '').replace(/\{venue_suffix\}/g, venueSuffix(values))
  return resolveMergeTokens(composed, values)
}

// ── Markers for the preview: highlight resolved tokens in sage ────────
// We do this by running a regex across the RESOLVED body and wrapping
// spans that match the resolved values. Cheap + good-enough-for-preview.
function highlightResolved(text, values) {
  if (!text) return []
  // Find short values worth highlighting (>2 chars, ≤60 chars).
  const marks = Object.entries(values)
    .filter(([, v]) => v && String(v).length >= 2 && String(v).length <= 60)
    .map(([, v]) => String(v))
    .sort((a, b) => b.length - a.length)
  if (!marks.length) return [{ text }]

  // Build a regex that matches any of the values (escaped).
  const escape = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${marks.map(escape).join('|')})`, 'g')
  const parts = []
  let last = 0
  let m
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index) })
    parts.push({ text: m[0], mark: true })
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push({ text: text.slice(last) })
  return parts
}

// ─────────────────────────────────────────────────────────────────────
export function QuickEmailModal({
  open,
  onClose,
  site,
  recipients = [],
  cc = [],
  bcc = [],
  bulkMode = false,
  context = {},
  contextLabel,
  contextMeta,
  triggeredFrom,
  supabase,
  userId,
  leadId,
  eventId,
  eventName,
  onHandoff,
  onToast,
  initialTemplateKey,
}) {
  // ── template selection ────────────────────────────────────────
  const available = useMemo(() => {
    const list = templatesForSite(site)
    return list.length ? list : QE_TEMPLATES.slice(0, 4)
  }, [site])

  const [templateKey, setTemplateKey] = useState(() =>
    initialTemplateKey || available[0]?.key || ''
  )

  // Reset to default when the modal is reopened with a different site
  useEffect(() => {
    if (!open) return
    setTemplateKey(initialTemplateKey || available[0]?.key || '')
  }, [open, site, initialTemplateKey, available])

  const template = useMemo(
    () => available.find(t => t.key === templateKey) || available[0] || null,
    [available, templateKey]
  )

  // ── token resolution ──────────────────────────────────────────
  const values = useMemo(() => buildTokenValues(context), [context])

  // ── editable subject + body (initialized from template) ───────
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [userDirty, setUserDirty] = useState(false)

  useEffect(() => {
    // Reset draft whenever the template changes, UNLESS the user has
    // already edited the content — don't stomp their draft.
    if (!template) return
    if (userDirty) return
    setSubject(applyTokens(template.subject, values))
    setBody(applyTokens(template.body, values))
  }, [template, values, userDirty])

  // Reset dirty flag + draft fields when modal closes
  useEffect(() => {
    if (!open) {
      setUserDirty(false)
    }
  }, [open])

  // ── keyboard: Esc to close, ⌘/Ctrl+Enter to send ──────────────
  const firstFieldRef = useRef(null)
  useEffect(() => {
    if (!open) return
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.() }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        doOpenInEmailClient()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subject, body, recipients, cc, bcc])

  // Focus first editable field when opened
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => firstFieldRef.current?.focus(), 40)
    return () => clearTimeout(t)
  }, [open])

  if (!open) return null

  // ── actions ───────────────────────────────────────────────────
  const recipientAddrs = recipients.map(r => r.email).filter(Boolean)
  const ccAddrs = cc.map(r => (typeof r === 'string' ? r : r.email)).filter(Boolean)
  const bccAddrs = bcc.map(r => (typeof r === 'string' ? r : r.email)).filter(Boolean)
  const recipientNames = recipients.map(r => r.name || r.email).filter(Boolean)
  // Bulk mode (vendor blast etc.) hides individual BCC addresses behind a
  // single pill — "BCC · 9 vendors" — so the modal doesn't feel like a
  // leaky mailing list. We still pass them to mailto so the planner's
  // client fans them out properly.
  const bccCount = bccAddrs.length
  const hasSendTarget = recipientAddrs.length > 0 || bccCount > 0

  function doOpenInEmailClient() {
    if (!hasSendTarget) return
    openInEmailClient({ to: recipientAddrs, cc: ccAddrs, bcc: bccAddrs, subject, body })
    // Log the hand-off (best-effort — swallows its own errors)
    if (supabase && userId) {
      logHandoff(supabase, {
        userId, leadId, eventId, eventName,
        templateKey: template?.key,
        templateLabel: template?.label,
        toCount: recipientAddrs.length + bccCount,
        subject,
        method: 'mailto',
        recipientNames: bulkMode
          ? [...recipientNames, ...(bccCount ? [`${bccCount} BCC`] : [])]
          : recipientNames,
      })
    }
    onHandoff?.({ method: 'mailto', templateKey: template?.key, subject, bccCount })
    onToast?.(bulkMode && bccCount ? `Opened — ${bccCount} recipient${bccCount === 1 ? '' : 's'} on BCC` : 'Opened in your email client')
    onClose?.()
  }

  async function doCopy() {
    const ok = await copyToClipboard({ subject, body, to: recipientAddrs.length ? recipientAddrs : bccAddrs })
    if (supabase && userId) {
      logHandoff(supabase, {
        userId, leadId, eventId, eventName,
        templateKey: template?.key,
        templateLabel: template?.label,
        toCount: recipientAddrs.length + bccCount,
        subject,
        method: 'copy',
        recipientNames,
      })
    }
    onHandoff?.({ method: 'copy', templateKey: template?.key, subject, bccCount })
    onToast?.(ok ? 'Copied draft to clipboard' : 'Clipboard unavailable — select + copy manually')
  }

  // ── style helpers ─────────────────────────────────────────────
  const resolvedParts = highlightResolved(body, values)
  const subjectParts = highlightResolved(subject, values)

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(26, 29, 35, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '48px 24px 24px',
        overflow: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 720,
          background: CARD, borderRadius: 18, border: `1px solid ${BORDER}`,
          boxShadow: '0 24px 80px rgba(26,29,35,0.18)',
          overflow: 'hidden', fontFamily: 'inherit', color: INK,
        }}
      >
        {/* ── context strip (who / why) ──────────────────────────── */}
        {(contextLabel || triggeredFrom) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '14px 24px', background: PAPER, borderBottom: `1px solid ${BORDER}`,
            fontSize: 13,
          }}>
            {contextLabel && (
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{contextLabel}</div>
                {contextMeta && <div style={{ fontSize: 12, color: MUTE }}>{contextMeta}</div>}
              </div>
            )}
            {triggeredFrom && (
              <div style={{
                fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: MUTE, fontWeight: 500,
              }}>
                {triggeredFrom}
              </div>
            )}
          </div>
        )}

        {/* ── header ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '18px 24px 14px', borderBottom: `1px solid ${BORDER}`,
        }}>
          <div>
            <div style={{
              fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: MUTE, fontWeight: 500, marginBottom: 2,
            }}>
              Quick Email · {template?.label || 'Draft'}
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 500 }}>
              {bulkMode && bccCount
                ? `To ${bccCount} recipient${bccCount === 1 ? '' : 's'} · BCC`
                : (
                  <>
                    To {recipientNames[0] || 'recipient'}
                    {recipientNames.length > 1 && ` +${recipientNames.length - 1}`}
                  </>
                )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', color: MUTE,
              fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* ── template chips ─────────────────────────────────────── */}
        {available.length > 1 && (
          <div style={{
            display: 'flex', gap: 6, padding: '14px 24px 0', flexWrap: 'wrap',
          }}>
            {available.map(t => {
              const active = t.key === templateKey
              return (
                <button
                  key={t.key}
                  onClick={() => { setTemplateKey(t.key); setUserDirty(false) }}
                  style={{
                    fontSize: 12, padding: '6px 12px', borderRadius: 999,
                    border: `1px solid ${active ? INK : BORDER}`,
                    background: active ? INK : PAPER,
                    color: active ? PAPER : INK_SOFT,
                    fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {t.label}
                </button>
              )
            })}
          </div>
        )}

        {/* ── fields: TO, SUBJECT ────────────────────────────────── */}
        <div style={{ padding: '14px 24px 0' }}>
          <FieldRow label={bulkMode && bccCount ? 'BCC' : 'To'}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {bulkMode && bccCount > 0 && recipients.length === 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'rgba(122,139,111,0.12)', border: `1px solid ${BORDER}`,
                  borderRadius: 999, padding: '4px 12px 4px 6px', fontSize: 12,
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', background: SAGE, color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontFamily: SERIF, fontWeight: 500, letterSpacing: 0.2,
                  }}>
                    {bccCount}
                  </span>
                  <span style={{ fontWeight: 500, color: INK }}>
                    {bccCount} recipient{bccCount === 1 ? '' : 's'}
                  </span>
                  <span style={{ color: MUTE, fontSize: 11 }}>
                    · addresses hidden — each sees only their own name
                  </span>
                </span>
              )}
              {!(bulkMode && bccCount) && recipients.length === 0 && (
                <span style={{ fontSize: 13, color: MUTE }}>(no recipient)</span>
              )}
              {recipients.map((r, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: PAPER, border: `1px solid ${BORDER}`,
                  borderRadius: 999, padding: '3px 10px 3px 4px', fontSize: 12,
                }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: i % 2 === 0 ? GOLD : SAGE, color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontFamily: SERIF,
                  }}>
                    {(r.name || r.email || '?').slice(0, 2).toUpperCase()}
                  </span>
                  <span style={{ fontWeight: 500 }}>{r.name || r.email}</span>
                  {r.email && r.name && (
                    <span style={{ color: MUTE }}>&lt;{r.email}&gt;</span>
                  )}
                </span>
              ))}
              {bulkMode && bccCount > 0 && recipients.length > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'rgba(122,139,111,0.12)', border: `1px solid ${BORDER}`,
                  borderRadius: 999, padding: '3px 10px', fontSize: 12, color: SAGE, fontWeight: 500,
                }}>
                  + {bccCount} BCC
                </span>
              )}
            </div>
          </FieldRow>

          <FieldRow label="Subject" last>
            <input
              ref={firstFieldRef}
              value={subject}
              onChange={e => { setSubject(e.target.value); setUserDirty(true) }}
              style={{
                width: '100%', background: 'transparent', border: 'none', outline: 'none',
                fontSize: 15, fontWeight: 500, color: INK, fontFamily: 'inherit',
                padding: 0,
              }}
            />
            {/* resolved-token preview strip below the input */}
            <div style={{
              fontSize: 11, color: MUTE, marginTop: 4, letterSpacing: 0,
            }}>
              {subjectParts.map((p, i) => p.mark ? (
                <span key={i} style={{ background: 'rgba(122,139,111,0.12)', color: SAGE, padding: '1px 4px', borderRadius: 3 }}>{p.text}</span>
              ) : (
                <span key={i}>{p.text}</span>
              ))}
            </div>
          </FieldRow>
        </div>

        {/* ── body ───────────────────────────────────────────────── */}
        <textarea
          value={body}
          onChange={e => { setBody(e.target.value); setUserDirty(true) }}
          rows={12}
          style={{
            width: '100%', border: 'none', outline: 'none',
            fontSize: 14, lineHeight: 1.65, color: INK_SOFT,
            padding: '14px 24px', fontFamily: 'inherit', resize: 'vertical',
            borderTop: `1px solid ${BORDER}`, background: CARD,
            boxSizing: 'border-box',
          }}
        />

        {/* ── body preview with sage-highlighted resolved tokens ── */}
        <div style={{
          padding: '0 24px 14px', fontSize: 11, color: MUTE, lineHeight: 1.5,
        }}>
          <span style={{
            display: 'inline-block', marginBottom: 4,
            letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
          }}>
            Resolved from context:
          </span>
          <div style={{ fontSize: 12, color: INK_SOFT, lineHeight: 1.55 }}>
            {resolvedParts.slice(0, 60).map((p, i) => p.mark ? (
              <span key={i} style={{ background: 'rgba(122,139,111,0.12)', color: SAGE, padding: '1px 4px', borderRadius: 3, fontWeight: 500 }}>{p.text}</span>
            ) : (
              <span key={i} style={{ color: MUTE }}>{p.text}</span>
            ))}
          </div>
        </div>

        {/* ── footer actions ─────────────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 24px', borderTop: `1px solid ${BORDER}`, background: PAPER,
        }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', fontSize: 12, color: MUTE }}>
            <Kbd>⌘ ↵</Kbd><span>Open in email client</span>
            <span style={{ color: SAGE, fontWeight: 500 }}>
              · replies thread in your inbox
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={doCopy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 999, fontSize: 13, fontWeight: 500,
                fontFamily: 'inherit', border: `1px solid ${BORDER}`, background: 'transparent',
                color: INK, cursor: 'pointer',
              }}
            >
              Copy
            </button>
            <button
              onClick={doOpenInEmailClient}
              disabled={!hasSendTarget}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 999, fontSize: 13, fontWeight: 500,
                fontFamily: 'inherit', border: 'none',
                background: hasSendTarget ? CLAY : BORDER,
                color: hasSendTarget ? '#fff' : MUTE,
                cursor: hasSendTarget ? 'pointer' : 'not-allowed',
                boxShadow: hasSendTarget ? '0 2px 10px rgba(198,93,69,0.22)' : 'none',
              }}
              onMouseEnter={e => { if (hasSendTarget) e.currentTarget.style.background = CLAY_DK }}
              onMouseLeave={e => { if (hasSendTarget) e.currentTarget.style.background = CLAY }}
            >
              {bulkMode && bccCount ? `Send to ${bccCount}` : 'Open in email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function FieldRow({ label, last, children }) {
  return (
    <div style={{
      display: 'flex', gap: 14, padding: '10px 0', alignItems: 'baseline',
      borderBottom: last ? 'none' : `1px solid ${BORDER}`,
    }}>
      <div style={{
        width: 70, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: MUTE, fontWeight: 600, flexShrink: 0,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  )
}

function Kbd({ children }) {
  return (
    <span style={{
      background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 4,
      padding: '1px 6px', fontFamily: 'ui-monospace, monospace', fontSize: 10, color: INK,
    }}>
      {children}
    </span>
  )
}

export default QuickEmailModal
